// Backlog.js
import React, { useState, useEffect, useRef } from 'react';
import { onSnapshot, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, TASK_STATUS } from '../firebase.config.js';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useAgency } from '../AgencyContext';
import { useTaskTracker } from '../TaskTrack/useTaskTracker';
import "./Backlog.css";
import SkeletonBacklog from './SkeletonBacklog.js';
import AlertBanner from '../Notification/AlertBanner.js';
import DescriptionTooltip from './DescriptionTooltip.js';

// Composant Timer pour afficher le décompte
const Timer = React.memo(({ task }) => {
  const [timeLeft, setTimeLeft] = useState(24 * 60 * 60); // 24 heures en secondes
  
  const [frozenStartTime] = useState(() => {
    console.log(`Timer init for task ${task.id}:`, {
      status: task.status,
      statusChangedAt: task.statusChangedAt?.toDate?.(),
      createdAt: task.createdAt?.toDate?.()
    });
    
    // Utiliser statusChangedAt en priorité, puis fallback sur createdAt pour L1
    let startTime;
    
    if (task.statusChangedAt) {
      // Si le champ statusChangedAt existe, l'utiliser (il ne change que lors des vrais changements de statut)
      startTime = task.statusChangedAt.toDate();
    } else {
      // Fallback pour les anciennes tâches sans statusChangedAt
      switch (task.status) {
        case TASK_STATUS.NOT_STARTED_L1:
          startTime = task.createdAt?.toDate?.() || new Date();
          break;
        case TASK_STATUS.PENDING_QA_L1:
        case TASK_STATUS.PENDING_QA_L2:
          // Pour les tâches QA existantes, utiliser un timestamp unique basé sur l'ID de la tâche
          // Cela évite que toutes les tâches QA aient le même startTime
          const taskIdHash = task.id.slice(-8); // Prendre les 8 derniers caractères de l'ID
          const hashNum = parseInt(taskIdHash, 36) % 3600; // Convertir en nombre et mod 3600 (1h en secondes)
          startTime = new Date(Date.now() - hashNum * 1000); // Soustraire ce nombre de secondes
          break;
        default:
          // Pour les autres statuts, utiliser un timestamp basé sur l'ID aussi
          const taskIdHash2 = task.id.slice(-8);
          const hashNum2 = parseInt(taskIdHash2, 36) % 3600;
          startTime = new Date(Date.now() - hashNum2 * 1000);
      }
    }
    
    console.log(`Timer startTime for task ${task.id}:`, startTime);
    return startTime;
  });
  
  useEffect(() => {
    const updateTimer = () => {
      const now = new Date();
      const elapsedSeconds = Math.floor((now - frozenStartTime) / 1000);
      const totalSeconds = 24 * 60 * 60; // 24 heures en secondes
      const remaining = Math.max(0, totalSeconds - elapsedSeconds);
      setTimeLeft(remaining);
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    
    return () => clearInterval(interval);
  }, []); // Pas de dépendances - le timer ne se relance jamais
  
  // Ne pas afficher le timer pour les statuts non concernés
  const shouldShowTimer = [
    TASK_STATUS.NOT_STARTED_L1,
    TASK_STATUS.NOT_STARTED_L2,
    TASK_STATUS.PENDING_QA_L1,
    TASK_STATUS.PENDING_QA_L2,
    TASK_STATUS.TO_BE_REWORKED_L1,
    TASK_STATUS.TO_BE_REWORKED_L2
  ].includes(task.status);
  
  if (!shouldShowTimer) {
    return <span className="backlog__timer backlog__timer__inactive">-</span>;
  }
  
  
  // Convertir les secondes restantes en format HH:MM:SS
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const isUrgent = timeLeft <= 3600; // Urgent quand il reste moins d'1 heure
  const isExpired = timeLeft === 0;
  
  return (
    <span className={`backlog__timer ${
      isExpired ? 'backlog__timer__expired' : 
      isUrgent ? 'backlog__timer__urgent' : 
      'backlog__timer__active'
    }`}>
      {isExpired ? '⏰' : formatTime(timeLeft)}
    </span>
  );
}, (prevProps, nextProps) => {
  // Ne re-render que si les champs importants pour le timer changent
  return (
    prevProps.task.id === nextProps.task.id &&
    prevProps.task.status === nextProps.task.status &&
    prevProps.task.statusChangedAt?.toDate?.()?.getTime() === nextProps.task.statusChangedAt?.toDate?.()?.getTime() &&
    prevProps.task.createdAt?.toDate?.()?.getTime() === nextProps.task.createdAt?.toDate?.()?.getTime()
  );
});

const Backlog = () => {
  const [tasks, setTasks] = useState([]);
  const [activeTab, setActiveTab] = useState('L1');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [user] = useAuthState(auth);
  const { currentAgency, getAgencyCollection, getAgencyDoc } = useAgency();
  const { trackEvent } = useTaskTracker();
  
  const userRole = currentAgency?.userRole;

  useEffect(() => {
    if (!currentAgency) return;
    
    
    const taskbacklogCollection = getAgencyCollection('taskbacklog');
    if (!taskbacklogCollection) return;
    
    const unsubscribe = onSnapshot(taskbacklogCollection, async (snapshot) => {
      const tasksData = [];
      
      for (const docSnap of snapshot.docs) {
        const taskData = { id: docSnap.id, ...docSnap.data() };
        
        // Enrichir avec les infos de l'IA
        try {
          const aiDocRef = getAgencyDoc('aimodel', taskData.aiModelId);
          if (aiDocRef) {
            const aiDoc = await getDoc(aiDocRef);
            if (aiDoc.exists()) {
              const aiData = aiDoc.data();
              taskData.aiName = aiData.name;
              // Si pas de photo dans la tâche, prendre celle du modèle IA
              if (!taskData.profilePhotoUrl && aiData.photoURL) {
                taskData.profilePhotoUrl = aiData.photoURL;
              }
              // Si pas de nom de profil, prendre celui du modèle IA
              if (!taskData.profileName && aiData.name) {
                taskData.profileName = aiData.name;
              }
            }
          }
        } catch (error) {
          console.error('Error loading AI info:', error);
        }
        
        tasksData.push(taskData);
      }
      
      setTasks(tasksData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentAgency, getAgencyCollection, getAgencyDoc]);

  const getTasksForTab = (tab) => {
    let filteredTasks = [];
    
    switch (tab) {
      case 'L1':
        filteredTasks = tasks.filter(task => 
          [TASK_STATUS.NOT_STARTED_L1, TASK_STATUS.TO_BE_REWORKED_L1].includes(task.status)
        );
        break;
      case 'L2':
        filteredTasks = tasks.filter(task => 
          [TASK_STATUS.NOT_STARTED_L2, TASK_STATUS.TO_BE_REWORKED_L2].includes(task.status)
        );
        break;
      case 'QA':
        filteredTasks = tasks.filter(task => 
          [TASK_STATUS.PENDING_QA_L1, TASK_STATUS.PENDING_QA_L2].includes(task.status)
        );
        break;
      case 'Completed':
        filteredTasks = tasks.filter(task => 
          [TASK_STATUS.VALIDATED_L2, TASK_STATUS.VALIDATED_L1].includes(task.status)
        );
        break;
      case 'All':
        filteredTasks = tasks;
        break;
      default:
        filteredTasks = [];
    }

    // Apply search filter
    if (searchTerm) {
      filteredTasks = filteredTasks.filter(task => 
        task.taskName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.aiName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.profileName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.createdBy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        formatUserName(task.createdBy)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.assignedTo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        formatUserName(task.assignedTo)?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filteredTasks.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'createdAt') {
        aVal = a.createdAt?.toDate?.() || new Date(0);
        bVal = b.createdAt?.toDate?.() || new Date(0);
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filteredTasks;
  };

  const formatUserName = (email) => {
    if (!email || typeof email !== 'string') return email;
    return email.split('@')[0];
  };

  const getTaskLevel = (status) => {
    if (status.includes('L1') || status === TASK_STATUS.GENERATED) {
      return { level: 'L1', class: 'level-l1' };
    } else if (status.includes('L2')) {
      return { level: 'L2', class: 'level-l2' };
    } else if (status.includes('QA')) {
      return { level: 'QA', class: 'level-qa' };
    }
    return { level: 'N/A', class: 'level-default' };
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      [TASK_STATUS.NOT_STARTED_L1]: { label: 'Not started', color: 'blue' },
      [TASK_STATUS.TO_BE_REWORKED_L1]: { label: 'Rework needed', color: 'red' },
      [TASK_STATUS.NOT_STARTED_L2]: { label: 'Not started', color: 'blue' },
      [TASK_STATUS.TO_BE_REWORKED_L2]: { label: 'Rework needed', color: 'red' },
      [TASK_STATUS.PENDING_QA_L1]: { label: 'Review QA L1', color: 'orange' },
      [TASK_STATUS.PENDING_QA_L2]: { label: 'Review QA L2', color: 'orange' },
      [TASK_STATUS.VALIDATED_L1]: { label: 'Completed L1', color: 'green' },
      [TASK_STATUS.VALIDATED_L2]: { label: 'Completed L2', color: 'green' },
    };
    return statusMap[status] || { label: status, color: 'grey' };
  };

  const getCurrentAssignmentInfo = (task) => {
    switch (task.status) {
      case TASK_STATUS.NOT_STARTED_L1:
      case TASK_STATUS.TO_BE_REWORKED_L1:
        return { 
          role: 'L1', 
          assignedTo: task.assignedTo || 'Unassigned',
          canTake: !task.assignedTo
        };
      case TASK_STATUS.NOT_STARTED_L2:
      case TASK_STATUS.TO_BE_REWORKED_L2:
        return { 
          role: 'L2', 
          assignedTo: task.assignedTo || 'Unassigned',
          canTake: !task.assignedTo
        };
      case TASK_STATUS.PENDING_QA_L1:
      case TASK_STATUS.PENDING_QA_L2:
        return { 
          role: 'QA', 
          assignedTo: task.assignedTo ? formatUserName(task.assignedTo) : 'Awaiting review',
          canTake: !task.assignedTo
        };
      case TASK_STATUS.VALIDATED_L1:
      case TASK_STATUS.VALIDATED_L2:
        return { 
          role: 'Completed', 
          assignedTo: 'Validated by ' + formatUserName(task.lastModifiedBy || 'QA'),
          canTake: false
        };
      default:
        return { 
          role: 'Unknown', 
          assignedTo: 'Unknown status',
          canTake: false
        };
    }
  };

  const canUserTakeTask = (task) => {
    if (!user || !userRole) return false;
    
    const assignmentInfo = getCurrentAssignmentInfo(task);
    
    if (!assignmentInfo.canTake) return false;
    if (userRole !== assignmentInfo.role) return false;
    
    switch (userRole) {
      case 'L1':
        return activeTab === 'L1' && 
               [TASK_STATUS.NOT_STARTED_L1, TASK_STATUS.TO_BE_REWORKED_L1].includes(task.status);
      case 'L2':
        return activeTab === 'L2' && 
               [TASK_STATUS.NOT_STARTED_L2, TASK_STATUS.TO_BE_REWORKED_L2].includes(task.status);
      case 'QA':
        return activeTab === 'QA' && 
               [TASK_STATUS.PENDING_QA_L1, TASK_STATUS.PENDING_QA_L2].includes(task.status);
      default:
        return false;
    }
  };

  const openTask = async (taskId, shouldAssign = false) => {
    if (shouldAssign && user) {
      try {
        const taskDocRef = getAgencyDoc('taskbacklog', taskId);
        if (!taskDocRef) return;
        
        await updateDoc(taskDocRef, {
          assignedTo: user.email,
          assignedAt: serverTimestamp(),
          lastModified: serverTimestamp(),
          lastModifiedBy: user.email,
        });
        
        // Find the task to get its name for tracking
        const taskToTrack = tasks.find(t => t.id === taskId);
        if (taskToTrack) {
          await trackEvent(taskId, taskToTrack.taskName, 'assigned himself', userRole, taskToTrack.quantity);
        }
        
        setTimeout(() => {
          window.location.href = `/task/${taskId}`;
        }, 100);
      } catch (error) {
        console.error("Error assigning task:", error);
        alert("❌ Error assigning task");
      }
    } else {
      window.location.href = `/task/${taskId}`;
    }
  };

  const getProgress = (task) => {
    let imageCount = 0;
    
    if ([TASK_STATUS.NOT_STARTED_L1, TASK_STATUS.TO_BE_REWORKED_L1, TASK_STATUS.PENDING_QA_L1].includes(task.status)) {
      imageCount = task.images?.filter(img => !img.isL2Image).length || 0;
    }
    else if ([TASK_STATUS.NOT_STARTED_L2, TASK_STATUS.TO_BE_REWORKED_L2, TASK_STATUS.PENDING_QA_L2, TASK_STATUS.VALIDATED_L2].includes(task.status)) {
      imageCount = task.images?.filter(img => img.isL2Image).length || 0;
    }
    else if (task.status === TASK_STATUS.VALIDATED_L1) {
      imageCount = task.images?.filter(img => !img.isL2Image).length || 0;
    }
    
    return `${imageCount}/${task.quantity}`;
  };

  const tabsData = [
    { key: 'L1', label: 'L1', count: getTasksForTab('L1').length, isRedBadge: true },
    { key: 'L2', label: 'L2', count: getTasksForTab('L2').length, isRedBadge: true },
    { key: 'QA', label: 'QA', count: getTasksForTab('QA').length, isRedBadge: true },
    { key: 'Completed', label: 'Completed', count: getTasksForTab('Completed').length, isRedBadge: false },
    { key: 'All', label: 'All', count: tasks.length, isRedBadge: false }
  ];

  if (loading) {
    return <SkeletonBacklog />;
  }

  return (
    <div className="backlog">
      {/* Welcome Banner */}
     {/*  <AlertBanner 
        bannerId="welcome_day1" 
        message="🚀 Hello everyone! Super excited to kick off Day 1 of the AI Factory with you all — lets make it an amazing journey together! 🙌 Marc & Octave"
      />
       */}
      {/* Header with tabs and search */}
      <header className="backlog__header">
        <div className="backlog__header-content">
          {/* Tabs on the left */}
          <nav className="backlog__tabs">
            {tabsData.map(tab => {
              const isActive = activeTab === tab.key;
              
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`backlog__tab ${isActive ? 'backlog__tab__active' : ''}`}
                >
                  <span className="backlog__tab-label">{tab.label}</span>
                  {tab.count > 0 && (
                    <span 
                      className={`backlog__notification-badge ${
                        tab.isRedBadge 
                          ? 'backlog__notification-badge__red' 
                          : 'backlog__notification-badge__grey'
                      }`}
                    >
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Search on the right */}
          <div className="backlog__search">
            <div className="backlog__search-wrapper">
              <svg className="backlog__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="M21 21l-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="backlog__search-input"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Data Table */}
      <main className="backlog__main">
        <div className="backlog__table-container">
          <table className="backlog__table">
            <thead className="backlog__table-head">
              <tr className="backlog__table-head-row">
                <th className="backlog__table-th">Task name</th>
                <th className="backlog__table-th">Profile</th>
                <th className="backlog__table-th">Level</th>
                <th className="backlog__table-th">Images</th>
                <th className="backlog__table-th">Creation date</th>
                <th className="backlog__table-th">Assignee</th>
                <th className="backlog__table-th">Timer</th>
                <th className="backlog__table-th">Status</th>
              </tr>
            </thead>
            <tbody className="backlog__table-body">
              {getTasksForTab(activeTab).map((task, index) => {
                const canTake = canUserTakeTask(task);
                const statusInfo = getStatusDisplay(task.status);
                const assignmentInfo = getCurrentAssignmentInfo(task);
                const isEvenRow = index % 2 === 0;
                
                return (
                  <tr 
                    key={task.id} 
                    className={`backlog__table-row ${isEvenRow ? 'backlog__table-row__even' : 'backlog__table-row__odd'}`}
                    onClick={() => openTask(task.id, canTake)}
                  >
                    <td className="backlog__table-td">
                      <div className="backlog__task-info">
                        <div className="backlog__task-name">
                          {task.taskName}
                          <DescriptionTooltip description={task.description} />
                        </div>
                      </div>
                    </td>
                    <td className="backlog__table-td">
                      <div className="backlog__profile-info">
                        {task.profilePhotoUrl ? (
                          <img 
                            src={task.profilePhotoUrl} 
                            alt={task.profileName || task.aiName} 
                            className="backlog__profile-photo"
                          />
                        ) : (
                          <div className="backlog__profile-placeholder">
                            {(() => {
                              const profileName = task.profileName || task.aiName || 'N/A';
                              // Extract emoji at the beginning (handles multi-character emojis like flags)
                              const emojiMatch = profileName.match(/^[\u{1F000}-\u{1F9FF}]|^[\u{1F600}-\u{1F64F}]|^[\u{1F300}-\u{1F5FF}]|^[\u{1F680}-\u{1F6FF}]|^[\u{1F1E0}-\u{1F1FF}][\u{1F1E0}-\u{1F1FF}]|^[\u{2600}-\u{26FF}]|^[\u{2700}-\u{27BF}]/u);
                              return emojiMatch ? emojiMatch[0] : profileName.charAt(0).toUpperCase();
                            })()}
                          </div>
                        )}
                        <span className="backlog__profile-name">
                          {task.profileName || task.aiName || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="backlog__table-td">
                      {(() => {
                        const levelInfo = getTaskLevel(task.status);
                        return (
                          <span className={`backlog__level ${levelInfo.class}`}>
                            {levelInfo.level}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="backlog__table-td">
                      <span className="backlog__image-count">
                        {getProgress(task)}
                      </span>
                    </td>
                    <td className="backlog__table-td">
                      <span className="backlog__created-date">
                        {(() => {
                          if (!task.createdAt) return 'N/A';
                          const date = task.createdAt.toDate();
                          return date.toLocaleDateString('en-US', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          });
                        })()}
                      </span>
                    </td>
                    <td className="backlog__table-td">
                      <div className="backlog__assignee-info">
                        <div className="backlog__assignee-name" title={assignmentInfo.assignedTo}>
                          {(() => {
                            const formattedName = formatUserName(assignmentInfo.assignedTo);
                            return formattedName.length > 10 
                              ? `${formattedName.substring(0, 10)}...` 
                              : formattedName;
                          })()}
                        </div>
                        {assignmentInfo.canTake && (
                          <div className="backlog__availability"></div>
                        )}
                      </div>
                    </td>
                    <td className="backlog__table-td">
                      <Timer key={`${task.id}-${task.status}`} task={task} />
                    </td>
                    <td className="backlog__table-td">
                      <span className={`backlog__status backlog__status__${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {getTasksForTab(activeTab).length === 0 && (
            <div className="backlog__empty">
              <div className="backlog__empty-text">Task is coming...</div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Backlog;